import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  getPendingCount,
  getPendingDistributionCount,
  getContractPendingCount,
} from "@/services/approvalService";
import { getUnresolvedCount } from "@/services/syncFailureService";

import { useAuth } from "@/contexts/AuthContext";

const REFETCH_MS = 30_000;

/**
 * Single source of truth for every "needs attention" count in the admin area.
 * Both the sidebar badges and the dashboard widgets read from here so the two
 * can never disagree. Every query is a HEAD count — no row payloads.
 */
export interface AdminAttentionCounts {
  /** Approval queue items, including pending distribution for platform admins. */
  approvals: number;
  /** Pending queue episodes blocked on an unsigned podcaster contract. */
  contracts: number;
  /** Non-privileged signups still awaiting content-creation review. */
  usersPendingReview: number;
  /** Support tickets still in the "open" state. */
  supportTickets: number;

  /** Bug reports that are neither resolved nor closed. */
  bugReports: number;
  /** Withdrawals awaiting admin action. */
  withdrawals: number;
  /** Unresolved platform sync failures. */
  failedSyncs: number;
  /** Studio bookings awaiting approval. */
  studioBookings: number;
  /** Job applications not yet reviewed. */
  jobApplications: number;
}

async function countRows(
  table: string,
  apply: (q: any) => any,
): Promise<number> {
  const { count } = await apply(
    (supabase as any).from(table).select("id", { count: "exact", head: true }),
  );
  return count ?? 0;
}

/**
 * Users awaiting content-creation review. Platform and network admins are
 * excluded — the `can_create_content` flag is inert for them, so counting
 * them would inflate the tile against what /admin/users lists.
 */
async function countUsersPendingReview(): Promise<number> {
  const { data: privileged } = await supabase
    .from("user_roles")
    .select("user_id")
    .in("role", ["admin", "network_admin"]);

  const excluded = [...new Set((privileged ?? []).map((r) => r.user_id))];

  let query = supabase
    .from("profiles")
    .select("id", { count: "exact", head: true })
    .eq("can_create_content", false);

  if (excluded.length) {
    query = query.not("id", "in", `(${excluded.join(",")})`);
  }

  const { count } = await query;
  return count ?? 0;
}

export function useAdminAttentionCounts(): AdminAttentionCounts {
  const { hasRole } = useAuth();
  const isPlatformAdmin = hasRole("admin");

  const { data: approvalCount = 0 } = useQuery({
    queryKey: ["approval-pending-count"],
    queryFn: getPendingCount,
    refetchInterval: REFETCH_MS,
  });

  const { data: pendingDistributionCount = 0 } = useQuery({
    queryKey: ["approval-pending-distribution-count"],
    queryFn: getPendingDistributionCount,
    refetchInterval: REFETCH_MS,
    enabled: isPlatformAdmin,
  });

  const { data: contracts = 0 } = useQuery({
    queryKey: ["admin-count-contracts-pending"],
    queryFn: getContractPendingCount,
    refetchInterval: REFETCH_MS,
    enabled: isPlatformAdmin,
  });

  const { data: usersPendingReview = 0 } = useQuery({
    queryKey: ["admin-count-users-pending-review"],
    queryFn: countUsersPendingReview,
    refetchInterval: REFETCH_MS,
    enabled: isPlatformAdmin,
  });

  const { data: supportTickets = 0 } = useQuery({
    queryKey: ["admin-count-support-open"],
    queryFn: () => countRows("support_tickets", (q) => q.eq("status", "open")),
    refetchInterval: REFETCH_MS,
  });


  const { data: bugReports = 0 } = useQuery({
    queryKey: ["admin-count-bugs-open"],
    queryFn: () => countRows("bug_reports", (q) => q.not("status", "in", "(resolved,closed)")),
    refetchInterval: REFETCH_MS,
  });

  const { data: withdrawals = 0 } = useQuery({
    queryKey: ["admin-count-withdrawals-pending"],
    queryFn: () => countRows("withdrawals", (q) => q.in("status", ["pending", "processing"])),
    refetchInterval: REFETCH_MS,
    enabled: isPlatformAdmin,
  });

  const { data: failedSyncs = 0 } = useQuery({
    queryKey: ["admin-count-failed-syncs"],
    queryFn: getUnresolvedCount,
    refetchInterval: REFETCH_MS,
  });

  const { data: studioBookings = 0 } = useQuery({
    queryKey: ["admin-count-studio-bookings-pending"],
    queryFn: () => countRows("studio_bookings", (q) => q.eq("status", "pending_approval")),
    refetchInterval: REFETCH_MS,
  });

  const { data: jobApplications = 0 } = useQuery({
    queryKey: ["admin-count-job-applications-new"],
    queryFn: () => countRows("job_applications", (q) => q.eq("status", "new")),
    refetchInterval: REFETCH_MS,
  });

  return {
    approvals: approvalCount + (isPlatformAdmin ? pendingDistributionCount : 0),
    contracts,
    usersPendingReview,
    supportTickets,
    bugReports,
    withdrawals,
    failedSyncs,
    studioBookings,
    jobApplications,
  };
}
